import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { login as apiLogin } from '../services/authService';
import { SESSION_EXPIRED_MESSAGE } from '../utils/sessionExpiry';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordHasSpaces, setPasswordHasSpaces] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by ResetPassword after a successful reset (and any other page that redirects here with a note).
  const notice = location.state?.message;
  // Set when AuthContext ended a session the API refused (utils/sessionExpiry.js).
  const sessionExpired = new URLSearchParams(location.search).get('session') === 'expired';
  // Carry the typed email into the reset flow so the parent doesn't retype it.
  const forgotPasswordHref = `/forgot-password${email ? `?email=${encodeURIComponent(email.trim())}` : ''}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPasswordHasSpaces(false);
    if (password !== password.trim()) {
      setPasswordHasSpaces(true);
    }
    try {
      const result = await apiLogin(email, password);
      //console.log('here is the login result',result);
      if (result.success) {
        login(result.userData);  
        
        // Check subscription status
        if (result.userData.accessStatus === 'inactive') {
          navigate('/onboarding');
        } else {
          // Navigate based on isTutor status
          navigate(result.userData.isTutor ? '/tutor-upcoming-lessons' : '/upcoming-lessons');
        }
      } else {
        setError(result.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-[#5C3BD1] font-fredoka">
            Sign in to your account
          </h2>
        </div>
        {notice && (
          <div className="text-green-700 text-sm bg-green-50 p-3 rounded-md">{notice}</div>
        )}
        {sessionExpired && !notice && (
          <div role="alert" className="text-amber-800 text-sm bg-amber-50 border border-amber-200 p-3 rounded-md">
            {SESSION_EXPIRED_MESSAGE}
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <input type="hidden" name="remember" value="true" />
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-[#5C3BD1] focus:border-[#5C3BD1] focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-12 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-[#5C3BD1] focus:border-[#5C3BD1] focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {/* Helper text */}
            <p className="text-xs text-gray-500 mt-1">
              Passwords are case-sensitive and include all spaces you type.
            </p>
            {/* Warning message */}
            {passwordHasSpaces && (
              <div className="text-yellow-600 text-sm font-semibold mt-1">
                Your password has spaces at the beginning or end. Did you mean to include them?
              </div>
            )}
          </div>

          {error && (
            <div className="text-red-700 text-sm bg-red-50 p-3 rounded-md">
              <p>{error}</p>
              <p className="mt-1">
                <Link to={forgotPasswordHref} className="font-semibold underline text-[#5C3BD1] hover:text-[#2F1E6B]">
                  Reset your password
                </Link>
                {' '}— we'll send a code to your WhatsApp and email.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#5C3BD1] hover:bg-[#2F1E6B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5C3BD1] max-md:min-h-[44px]"
            >
              Sign in
            </button>
            <Link
              to={forgotPasswordHref}
              className="w-full flex justify-center py-2 px-4 border border-[#5C3BD1] text-sm font-medium rounded-md text-[#5C3BD1] bg-white hover:bg-[#F3F0FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5C3BD1]"
            >
              Forgot password? Reset it with a code
            </Link>
          </div>
        </form>
        <div className="text-sm text-center">
          <Link to="/register" className="font-medium text-[#5C3BD1] hover:text-[#2F1E6B]">
            Don't have an account? Book a free trial
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
